import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Building2, Users, Car, DollarSign, Search, RefreshCw,
  Crown, Shield, User, ChevronDown, ChevronRight, 
  Ban, CheckCircle, Trash2, Eye, Clock
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

export default function AdminCompanyManagement() {
  const { user } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({});
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalMembers: 0,
    activeCompanies: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/companies`);
      setCompanies(response.data.companies || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async (companyId) => {
    try {
      const response = await axios.get(`${API}/admin/companies/${companyId}/details`);
      setCompanyDetails(prev => ({ ...prev, [companyId]: response.data }));
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  };

  const toggleCompanyExpand = (companyId) => {
    if (expandedCompany === companyId) {
      setExpandedCompany(null);
    } else {
      setExpandedCompany(companyId);
      if (!companyDetails[companyId]) {
        fetchCompanyDetails(companyId);
      }
    }
  };

  const handleToggleStatus = async (companyId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'activate' : 'suspend';
    
    if (!window.confirm(`Are you sure you want to ${action} this company?`)) return;

    try {
      await axios.put(`${API}/admin/companies/${companyId}/status`, { is_active: newStatus });
      toast.success(`Company ${action}d successfully`);
      fetchCompanies();
    } catch (error) {
      toast.error(`Failed to ${action} company`);
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`Are you sure you want to DELETE "${companyName}"? This will remove all members from the company. This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/admin/companies/${companyId}`);
      toast.success('Company deleted successfully');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-gold-500" />;
      case 'manager': return <Shield className="w-4 h-4 text-blue-500" />;
      default: return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const filteredCompanies = companies.filter(company => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      company.name?.toLowerCase().includes(query) ||
      company.owner_name?.toLowerCase().includes(query) ||
      company.owner_email?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Company Management
          </h1>
          <p className="text-gray-500">Manage all limo companies on the platform</p>
        </div>
        <Button variant="outline" onClick={fetchCompanies}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalCompanies}</p>
                <p className="text-sm text-gray-500">Total Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeCompanies}</p>
                <p className="text-sm text-gray-500">Active Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalMembers}</p>
                <p className="text-sm text-gray-500">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gold-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-gold-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.totalRevenue?.toLocaleString() || 0}</p>
                <p className="text-sm text-gray-500">Platform Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search companies by name, owner..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="search-companies"
        />
      </div>

      {/* Companies List */}
      <div className="space-y-4">
        {filteredCompanies.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No Companies Found</h3>
              <p className="text-gray-400 mt-1">
                {searchQuery ? `No companies match "${searchQuery}"` : 'No companies have been created yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredCompanies.map((company) => (
            <Card key={company.id} className={`overflow-hidden ${!company.is_active ? 'opacity-60' : ''}`}>
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => toggleCompanyExpand(company.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedCompany === company.id ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{company.name}</CardTitle>
                        {!company.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                            Suspended
                          </span>
                        )}
                      </div>
                      <CardDescription>
                        Owner: {company.owner_name} ({company.owner_email})
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-xl font-bold">{company.member_count || 0}</p>
                      <p className="text-xs text-gray-500">Members</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold">{company.total_rides || 0}</p>
                      <p className="text-xs text-gray-500">Rides</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-green-600">
                        ${(company.total_earnings || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Revenue</p>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant={company.is_active ? "outline" : "default"}
                        className={company.is_active ? "text-orange-600 border-orange-200 hover:bg-orange-50" : "bg-green-600 hover:bg-green-700"}
                        onClick={() => handleToggleStatus(company.id, company.is_active)}
                      >
                        {company.is_active ? (
                          <><Ban className="w-4 h-4 mr-1" /> Suspend</>
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-1" /> Activate</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteCompany(company.id, company.name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>

              {expandedCompany === company.id && (
                <CardContent className="border-t bg-gray-50 dark:bg-gray-800/50">
                  {companyDetails[company.id] ? (
                    <div className="space-y-4 pt-4">
                      {/* Company Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Created</p>
                          <p className="font-medium">
                            {new Date(company.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Invite Code</p>
                          <p className="font-mono font-bold">{company.invite_code}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Phone</p>
                          <p className="font-medium">{company.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Address</p>
                          <p className="font-medium">{company.address || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Members */}
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Members ({companyDetails[company.id].members?.length || 0})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {companyDetails[company.id].members?.map((member) => (
                            <div 
                              key={member.id}
                              className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg"
                            >
                              {getRoleIcon(member.role)}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{member.user_name}</p>
                                <p className="text-xs text-gray-500 truncate">{member.user_email}</p>
                              </div>
                              <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${
                                member.role === 'owner' ? 'bg-gold-100 text-gold-700' :
                                member.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {member.role}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t">
                        <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                          <p className="text-2xl font-bold">{companyDetails[company.id].stats?.total_rides || 0}</p>
                          <p className="text-xs text-gray-500">Total Rides</p>
                        </div>
                        <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                          <p className="text-2xl font-bold">{companyDetails[company.id].stats?.completed_rides || 0}</p>
                          <p className="text-xs text-gray-500">Completed</p>
                        </div>
                        <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">
                            ${(companyDetails[company.id].stats?.total_revenue || 0).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">Total Revenue</p>
                        </div>
                        <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                          <p className="text-2xl font-bold text-blue-600">
                            {(companyDetails[company.id].stats?.total_event_hours || 0).toFixed(1)}h
                          </p>
                          <p className="text-xs text-gray-500">Event Hours</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
